/**
 * The small Markdown reader the in-app player guide uses (D79).
 *
 * It handles exactly the constructs `docs/player/*.md` is written in, and nothing more:
 * headings one to three, paragraphs, `**bold**`, links, images, `-` bullets and pipe
 * tables. That is a deliberate floor, not a first draft of a Markdown library: the guide
 * is written in this repo, so the parser and the pages move together, and no dependency
 * is added for six constructs.
 *
 * Pure: text in, nodes out, no DOM and no fetch. `GuideScreen.tsx` turns the nodes into
 * the app's own type and colours, and resolves image sources against the bundled files.
 */

/** A run of text inside a heading, paragraph, bullet or table cell. */
export type Inline =
  | { type: 'text'; text: string }
  | { type: 'strong'; text: string }
  /** A link to another guide page, e.g. `[the market](market.md)`. `page` is the file name. */
  | { type: 'page'; text: string; page: string }
  /** An http link. The screen opens these in a new tab. */
  | { type: 'external'; text: string; href: string }
  /** An image inside a line of text. Standalone images become a `figure` block instead. */
  | { type: 'image'; alt: string; src: string };

export type Block =
  | { type: 'heading'; level: 1 | 2 | 3; inline: Inline[] }
  | { type: 'paragraph'; inline: Inline[] }
  | { type: 'list'; items: Inline[][] }
  /** A paragraph that is nothing but an image: the guide's screenshots. */
  | { type: 'figure'; alt: string; src: string }
  | { type: 'table'; head: Inline[][]; rows: Inline[][][] };

/** One parsed page: its first heading, if it has one, and its blocks. */
export interface Doc {
  title: string | null;
  blocks: Block[];
}

/** `[text](target)`, `![alt](src)` and `**bold**`, matched left to right. */
const INLINE = /(!?)\[([^\]]*)\]\(([^)\s]*)\)|\*\*([^*]+)\*\*/g;

function linkNode(text: string, target: string): Inline {
  if (/^https?:\/\//i.test(target)) return { type: 'external', text, href: target };
  // A relative `.md` target is another guide page. The fragment, if any, is dropped:
  // the guide has no anchors, and a link that cannot be honoured should not look live.
  const page = target.split('#')[0] ?? '';
  if (page.endsWith('.md')) return { type: 'page', text, page };
  // Anything else (a mail link, a path out of the guide) is left as plain words rather
  // than rendered as a link the app cannot follow.
  return { type: 'text', text };
}

/** Parse one line of inline Markdown. Exported for the tests. */
export function parseInline(src: string): Inline[] {
  const out: Inline[] = [];
  let at = 0;
  INLINE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = INLINE.exec(src)) !== null) {
    if (m.index > at) out.push({ type: 'text', text: src.slice(at, m.index) });
    if (m[4] !== undefined) out.push({ type: 'strong', text: m[4] });
    else if (m[1] === '!') out.push({ type: 'image', alt: m[2] ?? '', src: m[3] ?? '' });
    else out.push(linkNode(m[2] ?? '', m[3] ?? ''));
    at = m.index + m[0].length;
  }
  if (at < src.length) out.push({ type: 'text', text: src.slice(at) });
  return out.filter((n) => n.type !== 'text' || n.text !== '');
}

/** `| a | b |` to its cells, with the outer pipes and the padding taken off. */
function cells(line: string): Inline[][] {
  const inner = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return inner.split('|').map((c) => parseInline(c.trim()));
}

const IS_TABLE_RULE = (line: string): boolean => /^\|[\s:|-]+\|$/.test(line.trim());
const IS_ROW = (line: string): boolean => line.trim().startsWith('|');

/** Parse a guide page. Unknown syntax is kept as the words it is written in. */
export function parseMarkdown(src: string): Doc {
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  const pushParagraph = (text: string): void => {
    const inline = parseInline(text);
    const only = inline.length === 1 ? inline[0] : undefined;
    if (only && only.type === 'image')
      blocks.push({ type: 'figure', alt: only.alt, src: only.src });
    else if (inline.length > 0) blocks.push({ type: 'paragraph', inline });
  };

  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (line.trim() === '') {
      i++;
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({
        type: 'heading',
        level: (heading[1] ?? '#').length as 1 | 2 | 3,
        inline: parseInline((heading[2] ?? '').trim()),
      });
      i++;
      continue;
    }
    if (line.trim() === '---') {
      // A horizontal rule carries no meaning in the guide; the headings do the dividing.
      i++;
      continue;
    }
    if (IS_ROW(line) && IS_TABLE_RULE(lines[i + 1] ?? '')) {
      const head = cells(line);
      i += 2;
      const rows: Inline[][][] = [];
      while (i < lines.length && IS_ROW(lines[i] ?? '')) {
        rows.push(cells(lines[i] ?? ''));
        i++;
      }
      blocks.push({ type: 'table', head, rows });
      continue;
    }
    if (/^[-*]\s+/.test(line.trim())) {
      const items: Inline[][] = [];
      while (i < lines.length && /^[-*]\s+/.test((lines[i] ?? '').trim())) {
        items.push(parseInline((lines[i] ?? '').trim().replace(/^[-*]\s+/, '')));
        i++;
      }
      blocks.push({ type: 'list', items });
      continue;
    }
    // A paragraph runs to the next blank line or the next block opener. Its lines are
    // joined with a space, the way Markdown wraps them.
    const para: string[] = [];
    while (i < lines.length) {
      const l = lines[i] ?? '';
      if (l.trim() === '' || /^#{1,3}\s/.test(l) || IS_ROW(l) || /^[-*]\s+/.test(l.trim())) break;
      para.push(l.trim());
      i++;
    }
    pushParagraph(para.join(' '));
  }

  const first = blocks.find((b) => b.type === 'heading');
  const title =
    first && first.type === 'heading'
      ? first.inline.map((n) => ('text' in n ? n.text : '')).join('')
      : null;
  return { title, blocks };
}
